import {
  BID_TECHNOLOGY_TYPE,
  CHART_INTERVAL,
  CHART_TYPE,
  COMPANY_CREDIT_PROFILE,
  CONTRACT_TYPE,
  ChargingEnergyResponsibility,
  CreditSupportUnit,
  DispatchRights,
  ISO,
  MeasurementBasis,
  OFFER_STATUS,
  PRODUCT_TYPE,
  PriceUnit,
  REC_PROGRAM,
  SettlementMethod,
  TbVariant,
  VolumeUnit,
} from '@/utils/enums';

import { baseApi } from './baseApi';
import {
  ApiTags,
  AuditTagIds,
  CommonTagIds,
  MarketDepthTagIds,
  OfferTagIds,
} from './tags';
import { MatchingCriteriaFlags } from './types';

export type OfferPositionTags = {
  matching247: boolean;
  basisSharing: boolean;
  bessOption: boolean;
  capacityRights: boolean;
  colocationOption: boolean;
  escalator: boolean;
  nodalSettlement: boolean;
  priceCap: boolean;
  priceCollar: boolean;
  priceFloor: boolean;
  realTimeDataAccess: boolean;
  ssa: boolean;
  suretyBond: boolean;
  taxCreditTransferability: boolean;
  upfrontPayment: boolean;
  upsideRevenueShare: boolean;
};

export type CreateOfferDTO = {
  projectId: string;
  hubZoneMatrixId?: number;
  productType: PRODUCT_TYPE;
  contractType: string;
  contractLegalEntity: string;
  /**
   * The company id behind `contractLegalEntity`. This is the identifier the backend binds the
   * offer to; the name is display only and goes stale if the company is renamed.
   *
   * Optional, and genuinely omitted in some flows rather than always sent: a legacy offer has no
   * id to seed the form with, and a copy-mode form whose id is missing resolves one from the name
   * once the legal-entity list loads. The backend resolves the name by itself when the id is
   * absent. It must be omitted entirely rather than sent empty - it is a UUID server-side, and an
   * empty string fails deserialization.
   */
  contractLegalEntityCompanyId?: string;
  internalReference?: string;
  iso: ISO;
  settlementPoint?: string;
  settlementPointIndex?: string;
  fixedPrice: number;
  tenorStartDate: string;
  tenorMonths: number;
  buyerFraction: number;
  creditProfile: string;
  creditProfileDescription?: string;
  sellerPostCodCollateral?: number;
  negativePricingMethod: string;
  applicableRecProgram: string;
  applicableTrackingSystem: string;
  recDeliveryMethodology: string;
  performanceGuaranteeMethod: string;
  status: string;
  delistedOnCreation: boolean;
  /**
   * The one energy matching criteria flag a seller may set. The other five are `BUYER_ONLY` and
   * `OfferValidator.validateMatchingCriteriaFlags` rejects any offer that relaxes one of them,
   * so this is the only energy flag on the request type. A seller can relax it only through the
   * API: the form has no control for it, because Applicable REC Program's Any value covers that
   * case, so the form defaults it to `true` and returns whatever an offer already holds. The four
   * BESS flags are further down.
   *
   * Required rather than optional, and deliberately not part of `MatchingCriteriaFlags`, because
   * `OfferMapper` resolves an omitted flag to `TRUE` and `OfferService.copyMutableFields` copies
   * it onto the entity — so omitting it on an update silently re-tightens an offer relaxed
   * through the API. It is deliberately absent from `UpdateOfferDTO`'s `Omit` list below, so the
   * update payload carries it too.
   *
   * Requiring it documents that intent and makes the field non-optional for any caller that builds
   * the payload without an assertion. It does not, however, catch the omission in the two builders
   * that use one: `mapAddFormDataToRequest` and `mapEditFormDataToRequest` both close their object
   * literal with `as CreateOfferDTO` / `as UpdateOfferDTO`, and an `as` assertion accepts a literal
   * that omits a required property. What actually keeps the flag correct today is that both
   * builders assign it outside their truthiness guards — a guard would drop the key exactly when
   * the flag is `false`, which is the only case that matters.
   *
   * `satisfies` would make the guarantee real, but neither builder can use it as written: both
   * assemble the request by mutation after the assertion, and `addPositionTagsToRequest` fills the
   * sixteen position tag fields last through an `asserts request is T & PositionTagFields`
   * signature, so `satisfies` reports both literals as incomplete (sixteen errors in that file).
   */
  recProgramIsMc: boolean;
  // BESS fields (RME-1339). An Energy offer sends none of them.
  settlementMethod?: SettlementMethod;
  tbVariant?: TbVariant;
  dispatchRights?: DispatchRights;
  scopeEnergy?: boolean;
  scopeCapacity?: boolean;
  scopeAncillary?: boolean;
  scopeTransmission?: boolean;
  scopeDemandResponse?: boolean;
  scopeBlackstart?: boolean;
  settlementMethodIsMc?: boolean;
  tbVariantIsMc?: boolean;
  dispatchRightsIsMc?: boolean;
  productScopeIsMc?: boolean;
  variableOmIncluded?: boolean;
  variableOmRate?: number;
  sharingThreshold?: number;
  sharePercentage?: number;
  contractedCapacity?: number;
  chargingEnergyResponsibility?: ChargingEnergyResponsibility;
  dailyCycleLimit?: number;
  annualCycleLimit?: number;
  cycleSpillover?: boolean;
  guaranteedAvailability?: number;
  measurementBasis?: MeasurementBasis;
  /**
   * The form never sends this: the backend takes a battery offer's node from its project's
   * Interconnection Point. It is typed only so that tests can assert its absence.
   *
   * `priceEscalation` is deliberately not typed at all, so the form cannot send it. The backend
   * stores a missing value as 0.
   */
  settlementNodeId?: number;
} & OfferPositionTags;

/**
 * `contractLegalEntityCompanyId` rebinds the offer to a different contract legal entity, and is
 * sent only when the seller picked a different one: the backend locks every negotiation on the
 * offer for a change, so an edit that resends the stored entity would hold them up for nothing.
 * Omitted means no change. See `mapEditFormDataToRequest`.
 */
export type UpdateOfferDTO = Omit<
  CreateOfferDTO,
  | 'projectId'
  | 'hubZoneMatrixId'
  | 'productType'
  | 'contractType'
  | 'contractLegalEntity'
  | 'iso'
  | 'settlementPoint'
  | 'delistedOnCreation'
>;

export type OfferDTO = {
  id: number;
  ticker: string;
  projectId: string;
  projectName: string;
  hubZoneMatrixId: number;
  productTypeId: PRODUCT_TYPE;
  productTypeName: string;
  contractTypeId: string;
  contractTypeName: string;
  feePayingPartyId: string;
  feePayingPartyName: string;
  companyName: string;
  contractLegalEntity: string;
  /** The bound entity's id. Null on a legacy offer with no key, and wherever the seller is masked. */
  contractLegalEntityCompanyId: string | null;
  /**
   * Whether the owner may change the contract legal entity now - false once a negotiation on the
   * offer has reached pending execution. Null for every viewer but the owner, and on the `PUT`
   * response, so the edit form reads it from a fresh `GET`.
   */
  contractLegalEntityEditable: boolean | null;
  internalReference?: string;
  isoId: ISO;
  isoName: string;
  settlementPoint: string;
  settlementPointIndexId: string;
  settlementPointIndexName: string;
  fixedPrice: number;
  /** The unit of `fixedPrice`, resolved by the backend from the product type. */
  priceUnit: PriceUnit;
  tenorStartDate: string;
  tenorMonths: number;
  availableCapacityAc: number;
  impliedCapacityFactor?: number | null;
  p50ForecastVolume?: number | null;
  /**
   * The unit of the offer's size, resolved by the backend: `MWH_PER_YEAR` for an energy offer
   * (its P50 forecast), `MW` for a BESS offer. It does not describe `availableCapacityAc`, which
   * is always MW.
   */
  volumeUnit: VolumeUnit;
  buyerFraction: number;
  creditProfileId: COMPANY_CREDIT_PROFILE;
  creditProfileName: string;
  sellerPostCodCollateral?: number;
  creditProfileDescription?: string;
  negativePricingMethodId: string;
  negativePricingMethodName: string;
  applicableRecProgramId: string;
  applicableRecProgramName: string;
  applicableTrackingSystemId: string;
  applicableTrackingSystemName: string;
  recDeliveryMethodologyId: string;
  recDeliveryMethodologyName: string;
  performanceGuaranteeMethodId: string;
  performanceGuaranteeMethodName: string;
  statusId: OFFER_STATUS;
  statusName: string;
  pendingStatusId: OFFER_STATUS | null;
  pendingStatusName: string | null;
  companyId: string;
  created: string;
  updated: string;
  createdBy: string;
  updatedBy: string;
  // BESS fields (RME-1339). Null on an Energy offer. Optional, as `impliedCapacityFactor` is, so
  // fixtures built before these fields existed still type-check.
  settlementMethodId?: SettlementMethod | null;
  /** Null on a hub offer, and wherever the response is masked. */
  settlementNodeId?: number | null;
  settlementNodeName?: string | null;
  tbVariantId?: TbVariant | null;
  dispatchRightsId?: DispatchRights | null;
  scopeEnergy?: boolean | null;
  scopeCapacity?: boolean | null;
  scopeAncillary?: boolean | null;
  scopeTransmission?: boolean | null;
  scopeDemandResponse?: boolean | null;
  scopeBlackstart?: boolean | null;
  settlementMethodIsMc?: boolean | null;
  tbVariantIsMc?: boolean | null;
  dispatchRightsIsMc?: boolean | null;
  productScopeIsMc?: boolean | null;
  variableOmIncluded?: boolean | null;
  variableOmRate?: number | null;
  sharingThreshold?: number | null;
  sharePercentage?: number | null;
  contractedCapacity?: number | null;
  /** The project's battery duration, resolved by the backend. */
  durationHours?: number | null;
  chargingEnergyResponsibilityId?: ChargingEnergyResponsibility | null;
  dailyCycleLimit?: number | null;
  annualCycleLimit?: number | null;
  cycleSpillover?: boolean | null;
  guaranteedAvailability?: number | null;
  measurementBasisId?: MeasurementBasis | null;
  creditSupportUnit?: CreditSupportUnit | null;
} & OfferPositionTags &
  MatchingCriteriaFlags;

export type OfferBasicDetails = {
  id: number;
  ticker: string;
  projectId: string;
  projectName: string;
  productTypeId: PRODUCT_TYPE;
  productTypeName: string;
  technologyId: BID_TECHNOLOGY_TYPE;
  technologyName: string;
  isoId: string;
  isoName: string;
  settlementPoint: string;
  availableCapacityAc: number;
  fixedPrice: number;
  /** The unit of `fixedPrice`, resolved by the backend from the product type. */
  priceUnit: PriceUnit;
  tenorStartDate: string;
  tenorMonths: number;
  internalReference: string;
  p50ForecastVolume?: number | null;
  /**
   * The unit of the offer's size, resolved by the backend: `MWH_PER_YEAR` for an energy offer
   * (its P50 forecast), `MW` for a BESS offer. It does not describe `availableCapacityAc`, which
   * is always MW.
   */
  volumeUnit: VolumeUnit;
  contractTypeId: CONTRACT_TYPE;
  contractTypeName: string;
  creditProfileId: COMPANY_CREDIT_PROFILE;
  creditProfileName: string;
  applicableRecProgramId: REC_PROGRAM;
  applicableRecProgramName: string;
  statusId: OFFER_STATUS;
  statusName: string;
  created: string;
  updated: string;
} & OfferPositionTags;

export type GetOffersParams = {
  companyId: string;
  statuses?: OFFER_STATUS[];
  productType?: PRODUCT_TYPE;
};

export type GetOfferParams = {
  id: number;
  companyId: string;
  sideView: string;
};

export type ExportOfferMetricsParams = {
  offerId: number;
  companyId: string;
  sideView: string;
  filename: string;
};

export type GetOfferMetricsParams = {
  offerId: number;
  chartType: CHART_TYPE;
  interval: CHART_INTERVAL;
  companyId: string;
  sideView: string;
};

export type CarbonFreeEnergyDataPoint = {
  timestamp: string;
  cfePercentRatio: number;
  generationMwh: number;
};

export type NodalBasisDataPoint = {
  timestamp: string;
  basisPricePerMwhAsgen: number;
  cumulativeNodalBasisDollars: number;
};

export type GenerationDataPoint = {
  timestamp: string;
  generationMwh: number;
  netCapacityFactorPercent: number;
};

export type CarbonReductionDataPoint = {
  timestamp: string;
  generationMwh: number;
  lmeRateMtCo2ePerMwh: number;
  carbonAbatedMtCo2e: number;
};

export type FloatingPriceDataPoint = {
  timestamp: string;
  baselineMean: number;
  baselineP05: number;
  baselineP95: number;
  highGasMean: number;
  highGasP05: number;
  highGasP95: number;
  lowGasMean: number;
  lowGasP05: number;
  lowGasP95: number;
};

type OfferInterval = {
  offerId: number;
  interval: CHART_INTERVAL;
};

export type CarbonFreeEnergyDTO = OfferInterval & {
  data: CarbonFreeEnergyDataPoint[];
};
export type NodalBasisDTO = OfferInterval & {
  data: NodalBasisDataPoint[];
};

export type GenerationDTO = OfferInterval & {
  nameplateCapacityMw: number;
  buyersFraction: number;
  forecast: GenerationDataPoint[];
  historical: GenerationDataPoint[];
};

export type CarbonReductionDTO = OfferInterval & {
  forecast: CarbonReductionDataPoint[];
  historical: CarbonReductionDataPoint[];
};

export type FloatingPriceDTO = OfferInterval & {
  fixedPrice: number | null;
  data: FloatingPriceDataPoint[];
};

export type SettlementForecastDataPoint = {
  timestamp: string;
  // Nodal settlement prices ($/MWh) — 9 series
  settlementBaselineMean: number;
  settlementBaselineP05: number;
  settlementBaselineP95: number;
  settlementHighGasMean: number;
  settlementHighGasP05: number;
  settlementHighGasP95: number;
  settlementLowGasMean: number;
  settlementLowGasP05: number;
  settlementLowGasP95: number;
  // Cumulative cash flows ($) — 9 series, pre-computed by BE
  cumulativeBaselineMean: number;
  cumulativeBaselineP05: number;
  cumulativeBaselineP95: number;
  cumulativeHighGasMean: number;
  cumulativeHighGasP05: number;
  cumulativeHighGasP95: number;
  cumulativeLowGasMean: number;
  cumulativeLowGasP05: number;
  cumulativeLowGasP95: number;
};

export type SettlementForecastDTO = OfferInterval & {
  fixedPrice: number | null;
  dataPoints: SettlementForecastDataPoint[];
};

export type ImpliedCarbonPriceDataPoint = {
  timestamp: string;
  lmeRate: number;
  baselineMean: number;
  baselineP05: number;
  baselineP95: number;
  highGasMean: number;
  highGasP05: number;
  highGasP95: number;
  lowGasMean: number;
  lowGasP05: number;
  lowGasP95: number;
  settlementBaselineMean: number;
  settlementHighGasMean: number;
  settlementLowGasMean: number;
};

export type ImpliedCarbonPriceDTO = OfferInterval & {
  fixedPrice: number | null;
  data: ImpliedCarbonPriceDataPoint[];
};

/** Maps each chart type to its API response shape (the full OfferMetricsDTO for that view). */
export type OfferMetricsDataByChartType = {
  [CHART_TYPE.CFE_PERCENTAGE]: CarbonFreeEnergyDTO;
  [CHART_TYPE.NODAL_BASIS]: NodalBasisDTO;
  [CHART_TYPE.GENERATION]: GenerationDTO;
  [CHART_TYPE.CARBON_REDUCTION]: CarbonReductionDTO;
  [CHART_TYPE.FLOATING_PRICE]: FloatingPriceDTO;
  [CHART_TYPE.SETTLEMENT_FORECAST]: SettlementForecastDTO;
  [CHART_TYPE.IMPLIED_CARBON_PRICE]: ImpliedCarbonPriceDTO;
};

export type OfferMetricsDTO =
  | CarbonFreeEnergyDTO
  | NodalBasisDTO
  | GenerationDTO
  | CarbonReductionDTO
  | FloatingPriceDTO
  | SettlementForecastDTO
  | ImpliedCarbonPriceDTO;

/** Result type for getOfferMetrics when chartType is known. Use with useGetOfferMetricsQueryTyped. */
export type OfferMetricsResult<T extends CHART_TYPE> =
  OfferMetricsDataByChartType[T];

const BASE_URL = '/api/v1/offers';

export const offerApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getOffers: build.query<OfferBasicDetails[], GetOffersParams>({
      query: ({ companyId, statuses, productType }) => {
        const searchParams = new URLSearchParams();

        if (statuses) {
          statuses.forEach(status => searchParams.append('statuses', status));
        }
        if (productType) {
          searchParams.append('productType', productType);
        }

        const queryString = searchParams.toString();
        const suffix = queryString ? `?${queryString}` : '';
        const url = `${BASE_URL}${suffix}`;

        return {
          url,
          headers: {
            'X-Company-Id': companyId,
          },
        };
      },
      providesTags: (result, error, { companyId }) => [
        {
          type: ApiTags.OFFERS,
          id: `${CommonTagIds.LIST}-${companyId}`,
        },
      ],
    }),
    getOffer: build.query<OfferDTO, GetOfferParams>({
      query: ({ id, companyId, sideView }) => ({
        url: `${BASE_URL}/${id}`,
        headers: {
          'X-Company-Id': companyId,
          'X-Side-View': sideView,
        },
      }),
      providesTags: (result, error, { id }) => [{ type: ApiTags.OFFERS, id }],
    }),
    createOffer: build.mutation<
      OfferDTO,
      { data: CreateOfferDTO; companyId: string }
    >({
      query: ({ data, companyId }) => ({
        url: BASE_URL,
        method: 'POST',
        body: data,
        headers: { 'X-Company-Id': companyId },
      }),
      invalidatesTags: (result, error, { companyId, data }) => [
        {
          type: ApiTags.OFFERS,
          id: `${CommonTagIds.LIST}-${companyId}`,
        },
        {
          type: ApiTags.OFFERS,
          id: `${OfferTagIds.PROJECT_OFFERS}-${data.projectId}-${companyId}`,
        },
        {
          type: ApiTags.MARKET_DEPTH,
          id: MarketDepthTagIds.MATCHING_OFFERS,
        },
        { type: ApiTags.MARKET_ACTIVITY },
        ...(result
          ? [
              {
                type: ApiTags.AUDIT as const,
                id: `${AuditTagIds.OFFER}-${result.id}`,
              },
            ]
          : []),
      ],
    }),
    updateOffer: build.mutation<
      OfferDTO,
      { id: number; data: UpdateOfferDTO; companyId: string; projectId: string }
    >({
      query: ({ id, data, companyId }) => ({
        url: `${BASE_URL}/${id}`,
        method: 'PUT',
        body: data,
        headers: { 'X-Company-Id': companyId },
      }),
      invalidatesTags: (result, error, { id, companyId, projectId, data }) => [
        { type: ApiTags.OFFERS, id },
        {
          type: ApiTags.OFFERS,
          id: `${CommonTagIds.LIST}-${companyId}`,
        },
        {
          type: ApiTags.OFFERS,
          id: `${OfferTagIds.PROJECT_OFFERS}-${projectId}-${companyId}`,
        },
        {
          type: ApiTags.MARKET_DEPTH,
          id: MarketDepthTagIds.MATCHING_OFFERS,
        },
        { type: ApiTags.MARKET_ACTIVITY },
        {
          type: ApiTags.AUDIT,
          id: `${AuditTagIds.OFFER}-${id}`,
        },
        // A contract legal entity change reaches every open negotiation on the offer: their
        // contract legal entity and Reporting Counterparty move, and contracting documents are
        // regenerated. The negotiation ids are not known here, so the whole types are refetched.
        // Only on success: RTK also invalidates on a rejected request, and a refused change (400,
        // 409) moved nothing.
        ...(!error && data.contractLegalEntityCompanyId
          ? [
              { type: ApiTags.NEGOTIATIONS },
              { type: ApiTags.CONTRACTING_NEGOTIATIONS },
            ]
          : []),
      ],
    }),
    deleteOffer: build.mutation<
      void,
      { id: string; companyId: string; projectId: string }
    >({
      query: ({ id, companyId }) => ({
        url: `${BASE_URL}/${id}`,
        method: 'DELETE',
        headers: { 'X-Company-Id': companyId },
      }),
      invalidatesTags: (result, error, { id, companyId, projectId }) => [
        {
          type: ApiTags.OFFERS,
          id: `${CommonTagIds.LIST}-${companyId}`,
        },
        {
          type: ApiTags.OFFERS,
          id: `${OfferTagIds.PROJECT_OFFERS}-${projectId}-${companyId}`,
        },
        {
          type: ApiTags.MARKET_DEPTH,
          id: MarketDepthTagIds.MATCHING_OFFERS,
        },
        {
          type: ApiTags.AUDIT,
          id: `${AuditTagIds.OFFER}-${id}`,
        },
      ],
    }),

    exportOfferMetrics: build.query<null, ExportOfferMetricsParams>({
      query: ({ offerId, companyId, sideView, filename }) => ({
        url: `${BASE_URL}/${offerId}/metrics/export`,
        headers: {
          'X-Company-Id': companyId,
          'X-Side-View': sideView,
        },
        responseHandler: async (response: Response) => {
          if (!response.ok) return response.json();
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
          return null;
        },
      }),
      keepUnusedDataFor: 0,
    }),

    getOfferMetrics: build.query<OfferMetricsDTO, GetOfferMetricsParams>({
      query: ({ offerId, chartType, interval, companyId, sideView }) => {
        const searchParams = new URLSearchParams();
        searchParams.append('view', chartType);
        searchParams.append('interval', interval);

        return {
          url: `${BASE_URL}/${offerId}/metrics/views?${searchParams.toString()}`,
          headers: {
            'X-Company-Id': companyId,
            'X-Side-View': sideView,
          },
        };
      },
      providesTags: (result, error, { offerId, chartType, interval }) => [
        {
          type: ApiTags.OFFERS,
          id: `${OfferTagIds.METRICS}-${offerId}-${chartType}-${interval}`,
        },
      ],
    }),
  }),
});

export const {
  useGetOffersQuery,
  useGetOfferQuery,
  useLazyGetOfferQuery,
  useCreateOfferMutation,
  useUpdateOfferMutation,
  useDeleteOfferMutation,
  useGetOfferMetricsQuery,
  useLazyExportOfferMetricsQuery,
} = offerApi;

type GetOfferMetricsQueryResult = ReturnType<typeof useGetOfferMetricsQuery>;

type GetOfferMetricsTypedResult<T extends CHART_TYPE> = Omit<
  GetOfferMetricsQueryResult,
  'data'
> & { data: OfferMetricsResult<T> | undefined };

/** Typed hook: return data is narrowed to the DTO for the given chartType. */
export function useGetOfferMetricsQueryTyped<T extends CHART_TYPE>(
  arg: GetOfferMetricsParams & { chartType: T },
  options?: Parameters<typeof useGetOfferMetricsQuery>[1]
): GetOfferMetricsTypedResult<T> {
  return useGetOfferMetricsQuery(
    arg,
    options
  ) as unknown as GetOfferMetricsTypedResult<T>;
}
